import useMousePosition from '#/hooks/useMousePosition';
import React, { useEffect, useRef, useState } from 'react';

interface IProperties {
  min: number;
  max: number;
  value: number;
  progress?: boolean;
  thumbVisible?: 'hover' | 'always';
  onChange: (newValue: number) => void;
}

const Slider = (properties: IProperties) => {
  const {
    min,
    max,
    value,
    progress,
    onChange,
    thumbVisible = 'always',
  } = properties;

  const [internalValue, setInternalValue] = useState(value);

  const progressBarReference = useRef<HTMLDivElement>(null);

  const [hoverBar, setHoverBar] = useState(false);

  const { mouseX } = useMousePosition();

  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!dragging) {
      setInternalValue(value);
    }
  }, [dragging, value]);

  useEffect(() => {
    const handleMouseUpAndLeave = () => {
      if (dragging) {
        onChange(internalValue);
        setDragging(false);
      }
    };

    document.addEventListener('mouseup', handleMouseUpAndLeave);
    document.addEventListener('mouseleave', handleMouseUpAndLeave);

    return () => {
      document.removeEventListener('mouseup', handleMouseUpAndLeave);
      document.removeEventListener('mouseleave', handleMouseUpAndLeave);
    };
  }, [dragging, internalValue, onChange]);

  useEffect(() => {
    if (dragging && progressBarReference.current) {
      const rect = progressBarReference.current.getBoundingClientRect();

      const x = mouseX - rect.left;

      const width = progressBarReference.current.clientWidth;

      const percentage = x / width;

      const newPosition = Math.round(percentage * (max - min));

      setInternalValue(percentage > 1 ? max : percentage < 0 ? 0 : newPosition);
    }
  }, [dragging, max, min, mouseX]);

  const percentageProgress = internalValue / (max - min);

  return (
    <div
      className="w-full relative flex items-center"
      onMouseEnter={() => setHoverBar(true)}
      onMouseLeave={() => setHoverBar(false)}
    >
      <div
        ref={progressBarReference}
        className="flex-1 h-1 bg-theme-lighter-gray rounded-full"
        onMouseDown={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();

          const x = event.clientX - rect.left;

          const width = event.currentTarget.clientWidth;

          const percentage = x / width;

          const newPosition = Math.round(percentage * (max - min));

          setInternalValue(newPosition);

          setDragging(true);
        }}
      >
        <div
          className={`h-full pointer-events-none ${
            hoverBar ? 'bg-theme-green' : 'bg-theme-white'
          } rounded-full ${progress ? 'visible' : 'invisible'}`}
          style={{
            width: `${(percentageProgress || 0) * 100}%`,
          }}
        />
      </div>
      <div
        className={`w-3 rounded-full pointer-events-none aspect-square bg-theme-white absolute ${
          (thumbVisible === 'hover' && hoverBar) || thumbVisible === 'always'
            ? 'visible'
            : 'invisible'
        }`}
        style={{
          left: progressBarReference.current?.clientWidth
            ? (percentageProgress || 0) *
              progressBarReference.current?.clientWidth
            : 0,
        }}
      />
    </div>
  );
};

export default Slider;
